import React, { useEffect, useMemo, useState } from 'react';
import useAppStore from '../../store/useAppStore.js';
import { getOrders, cancelOrder, getAdminUserOrderHistory, getAdminWaiterPerformance } from '../../services/orderApi.js';
import { getWaiters } from '../../services/waiterApi.js';
import { formatCurrencyShort } from '../../utils/amountHelpers.js';
import ConfirmDialog from '../common/ConfirmDialog.js';
import Pagination from '../common/Pagination.js';

const AdminOrders = () => {
  const { admin, loadUsers } = useAppStore((state) => ({
    admin: state.admin,
    loadUsers: state.admin.loadUsers
  }));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(100);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [orderCounts, setOrderCounts] = useState({ totalOrders: 0, completedOrders: 0, cancelledOrders: 0 });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, orderId: null });
  const [cancelingOrderId, setCancelingOrderId] = useState(null);
  const [waiters, setWaiters] = useState([]);
  const [waitersError, setWaitersError] = useState(null);
  const [userOrderHistory, setUserOrderHistory] = useState([]);
  const [waiterPerformanceData, setWaiterPerformanceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (!admin.usersLoaded && !admin.usersLoading) {
      loadUsers();
    }
  }, [admin.usersLoaded, admin.usersLoading, loadUsers]);

  useEffect(() => {
    const loadAdminOrders = async () => {
      setLoading(true);
      setError(null);
      setWaitersError(null);
      setStart(0);
      setEnd(100);
      setHasMoreOrders(true);
      setLoadingMore(false);
      try {
        const [orderResponse, waiterResponse, userHistoryResponse, waiterPerformanceResponse] = await Promise.all([
          getOrders({ start: 0, end: 100 }),
          getWaiters(),
          getAdminUserOrderHistory(),
          getAdminWaiterPerformance()
        ]);
        const initialOrders = orderResponse.orders || [];
        setOrders(initialOrders);
        setWaiters(waiterResponse.waiters || []);
        setUserOrderHistory(userHistoryResponse.history || []);
        setWaiterPerformanceData(waiterPerformanceResponse.performance || []);
        setOrderCounts({
          totalOrders: Number(orderResponse.counts?.totalOrders || 0),
          completedOrders: Number(orderResponse.counts?.completedOrders || 0),
          cancelledOrders: Number(orderResponse.counts?.cancelledOrders || 0)
        });
        setHasMoreOrders(initialOrders.length === 100);
      } catch (err) {
        const message = err.message || 'Unable to load orders';
        setError(message);
        if (!waiters.length) {
          setWaitersError(message);
        }
        setUserOrderHistory([]);
        setWaiterPerformanceData([]);
      } finally {
        setLoading(false);
      }
    };

    loadAdminOrders();
  }, []);

  const branchOptions = useMemo(() => [
    { label: 'All Branches', value: 'all' },
    ...(admin.branches || []).map((branch) => ({
      label: branch.name || branch.id,
      value: String(branch.id)
    }))
  ], [admin.branches]);

  const userOptions = useMemo(() => [
    { label: 'All Users', value: 'all' },
    ...((admin.users || []).map((user) => ({
      label: `${user.name} ${user.role ? `(${user.role})` : ''}`.trim(),
      value: String(user.id)
    })))
  ], [admin.users]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (selectedStatus !== 'all' && order.status !== selectedStatus) {
        return false;
      }
      if (selectedBranch !== 'all' && String(order.branch_id) !== selectedBranch) {
        return false;
      }
      if (selectedUser !== 'all' && String(order.user_id) !== selectedUser) {
        return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const orderId = String(order.id);
        const token = String(order.token_number || '').toLowerCase();
        const userName = String(order.cashier_name || '').toLowerCase();
        return (
          orderId.includes(query) ||
          token.includes(query) ||
          userName.includes(query)
        );
      }
      return true;
    });
  }, [orders, selectedBranch, selectedStatus, selectedUser, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, selectedBranch, selectedUser, searchQuery]);

  const loadMoreOrders = async () => {
    if (!hasMoreOrders || loadingMore) return;
    setLoadingMore(true);
    const nextStart = end;
    const nextEnd = end + 100;

    try {
      const orderResponse = await getOrders({ start: nextStart, end: nextEnd });
      const nextOrders = orderResponse.orders || [];
      setOrders((prevOrders) => [...prevOrders, ...nextOrders]);
      setEnd(nextEnd);
      setHasMoreOrders(nextOrders.length === 100);
      if (orderResponse.counts) {
        setOrderCounts({
          totalOrders: Number(orderResponse.counts.totalOrders || 0),
          completedOrders: Number(orderResponse.counts.completedOrders || 0),
          cancelledOrders: Number(orderResponse.counts.cancelledOrders || 0)
        });
      }
    } catch (err) {
      console.error('Failed to load more admin orders:', err);
      setError(err.message || 'Unable to load more orders');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const loadedPages = Math.ceil(orders.length / pageSize);
    if (
      !loading &&
      !loadingMore &&
      hasMoreOrders &&
      loadedPages > 0 &&
      currentPage === loadedPages
    ) {
      loadMoreOrders();
    }
  }, [currentPage, orders.length, hasMoreOrders, loading, loadingMore]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredOrders.slice(startIndex, startIndex + pageSize);
  }, [filteredOrders, currentPage, pageSize]);

  const userActivity = useMemo(() => {
    return (userOrderHistory || []).map((item) => ({
      userId: item.userId || 'unknown',
      userName: item.userName || 'Unknown User',
      total: Number(item.totalOrders || 0),
      completed: Number(item.completed || 0),
      cancelled: Number(item.cancelled || 0),
      pending: Number(item.pending || 0),
      lastOrderAt: item.lastOrderAt ? new Date(item.lastOrderAt) : null
    }));
  }, [userOrderHistory]);

  const getWaiterName = (waiterId) => {
    if (!waiterId) return 'N/A';
    const waiter = waiters.find((waiter) => String(waiter.id) === String(waiterId));
    return waiter ? waiter.name : `Waiter #${waiterId}`;
  };

  const waiterPerformance = useMemo(() => {
    return (waiterPerformanceData || []).map((item) => ({
      waiterId: item.waiterId || 'unknown',
      name: item.waiterName || `Waiter ${item.waiterId}`,
      total: Number(item.totalOrders || 0),
      completed: Number(item.completed || 0),
      cancelled: Number(item.cancelled || 0),
      pending: Number(item.pending || 0),
      revenue: Number(item.revenue || 0),
      lastOrderAt: item.lastOrderAt ? new Date(item.lastOrderAt) : null
    }));
  }, [waiterPerformanceData]);

  const handleCancelClick = (orderId) => {
    setConfirmDialog({ isOpen: true, orderId });
  };

  const handleConfirmCancel = async () => {
    const orderId = confirmDialog.orderId;
    if (!orderId) {
      setConfirmDialog({ isOpen: false, orderId: null });
      return;
    }

    setCancelingOrderId(orderId);
    try {
      await cancelOrder(orderId);
      setOrders((prev) => prev.map((order) => order.id === orderId ? { ...order, status: 'cancelled' } : order));
    } catch (err) {
      console.error('Order cancel failed:', err);
      setError(err.message || 'Failed to cancel order');
    } finally {
      setCancelingOrderId(null);
      setConfirmDialog({ isOpen: false, orderId: null });
    }
  };

  const statusOptions = [
    { label: 'All Orders', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' },
    { label: 'Cancelled', value: 'cancelled' }
  ];

  const formatCurrency = formatCurrencyShort;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Admin Orders</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Review order activity across branches, including cancelled orders and user history.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Total orders</div>
          <div className="mt-2 text-3xl font-semibold text-slate-900">{orderCounts.totalOrders}</div>
        </div>
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Cancelled orders</div>
          <div className="mt-2 text-3xl font-semibold text-rose-600">
            {orderCounts.cancelledOrders}
          </div>
        </div>
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">Completed orders</div>
          <div className="mt-2 text-3xl font-semibold text-emerald-600">
            {orderCounts.completedOrders}
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="grid gap-4 sm:grid-cols-[220px_180px_220px] w-full">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {branchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {userOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search order ID, token, user"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full min-w-[240px] rounded-3xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div className="mt-6 overflow-auto h-[400px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50 text-left text-sm text-gray-600">
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Created by</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white text-sm text-gray-700 overflow-y-auto">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                    Loading orders...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No orders match the selected filters.
                  </td>
                </tr>
              ) : (
                <>
                  {paginatedOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-4 py-4 font-semibold text-slate-900">#{order.order_number || order.id}</td>
                      <td className="px-4 py-4">{admin.branches?.find((b) => String(b.id) === String(order.branch_id))?.name || `Branch ${order.branch_id}`}</td>
                      <td className="px-4 py-4">{order.cashier_name || 'Unknown'}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          order.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          order.status === 'cancelled' ? 'bg-rose-100 text-rose-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">{formatCurrency(order.total)}</td>
                      <td className="px-4 py-4">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-2xl bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {order.token_number ? `Token ${order.token_number}` : 'No token'}
                          </span>
                          {order.status !== 'cancelled' && (
                            <button
                              type="button"
                              disabled={cancelingOrderId === order.id}
                              onClick={() => handleCancelClick(order.id)}
                              className="rounded-2xl bg-rose-500 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:bg-rose-200"
                            >
                              {cancelingOrderId === order.id ? 'Cancelling...' : 'Cancel'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {loadingMore && (
                    <tr>
                      <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                        Loading more orders...
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        {filteredOrders.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filteredOrders.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">User Order History</h2>
            <p className="text-sm text-gray-600">Track each user's order creation, cancellation, and completion totals.</p>
          </div>
        </div>

        <div className="overflow-auto h-[400px]">
          <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Total Orders</th>
                <th className="px-4 py-3">Completed</th>
                <th className="px-4 py-3">Cancelled</th>
                <th className="px-4 py-3">Pending / Active</th>
                <th className="px-4 py-3">Last Order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {userActivity.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No user order activity available.
                  </td>
                </tr>
              ) : (
                userActivity.map((activity) => (
                  <tr key={`${activity.userId}-${activity.lastOrderAt?.toISOString() || 'none'}`}>
                    <td className="px-4 py-4 font-medium text-slate-900">{activity.userName}</td>
                    <td className="px-4 py-4">{activity.total}</td>
                    <td className="px-4 py-4 text-emerald-700">{activity.completed}</td>
                    <td className="px-4 py-4 text-rose-700">{activity.cancelled}</td>
                    <td className="px-4 py-4 text-yellow-700">{activity.pending}</td>
                    <td className="px-4 py-4">{activity.lastOrderAt ? activity.lastOrderAt.toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Waiter Performance Overview</h2>
            <p className="text-sm text-gray-600">Get a quick view of waiter order volume, completion rate, and revenue.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-3xl bg-slate-50 p-4 text-sm text-gray-700">
              <div className="font-semibold text-slate-900">Waiters</div>
              <div className="mt-2 text-3xl font-semibold">{waiterPerformance.length}</div>
            </div>
            <div className="rounded-3xl bg-slate-50 p-4 text-sm text-gray-700">
              <div className="font-semibold text-slate-900">Total waiter orders</div>
              <div className="mt-2 text-3xl font-semibold">{waiterPerformance.reduce((sum, item) => sum + item.total, 0)}</div>
            </div>
            <div className="rounded-3xl bg-slate-50 p-4 text-sm text-gray-700">
              <div className="font-semibold text-slate-900">Top waiter</div>
              <div className="mt-2 text-3xl font-semibold">
                {waiterPerformance[0]?.name || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Waiter</th>
                <th className="px-4 py-3">Orders</th>
                <th className="px-4 py-3">Completed</th>
                <th className="px-4 py-3">Cancelled</th>
                <th className="px-4 py-3">Pending</th>
                <th className="px-4 py-3">Revenue</th>
                <th className="px-4 py-3">Avg order</th>
                <th className="px-4 py-3">Last Order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {waiterPerformance.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No waiter performance data available.
                  </td>
                </tr>
              ) : (
                waiterPerformance.map((waiter) => (
                  <tr key={waiter.waiterId}>
                    <td className="px-4 py-4 font-medium text-slate-900">{waiter.name}</td>
                    <td className="px-4 py-4">{waiter.total}</td>
                    <td className="px-4 py-4 text-emerald-700">{waiter.completed}</td>
                    <td className="px-4 py-4 text-rose-700">{waiter.cancelled}</td>
                    <td className="px-4 py-4 text-yellow-700">{waiter.pending}</td>
                    <td className="px-4 py-4">{formatCurrency(waiter.revenue)}</td>
                    <td className="px-4 py-4">{waiter.total ? formatCurrency(waiter.revenue / waiter.total) : formatCurrency(0)}</td>
                    <td className="px-4 py-4">{waiter.lastOrderAt ? waiter.lastOrderAt.toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, orderId: null })}
        onConfirm={handleConfirmCancel}
        title="Confirm Cancel Order"
        message="Are you sure you want to cancel this order? This action will mark the order as cancelled."
      />
    </div>
  );
};

export default AdminOrders;
